import React, { useEffect, useRef, useState } from 'react'
import {
  Alert,
  Avatar,
  Box,
  Button,
  Chip,
  CircularProgress,
  Divider,
  Grid,
  IconButton,
  Paper,
  Stack,
  TextField,
  Typography,
} from '@mui/material'
import {
  LocalPolice,
  Place,
  ReportProblem,
  Send,
} from '@mui/icons-material'
import moment from 'moment'
import API from '../../../services/API'

const statusChipStyles = {
  Available: { bg: '#DCFCE7', color: '#166534' },
  'Off Duty': { bg: '#E2E8F0', color: '#334155' },
  Break: { bg: '#DBEAFE', color: '#1E3A8A' },
  Unavailable: { bg: '#FCE7F3', color: '#85276A' },
  Assigned: { bg: '#FEE2E2', color: '#991B1B' },
  Acknowledged: { bg: '#FFEDD5', color: '#9A3412' },
  'On Route': { bg: '#FFEDD5', color: '#9A3412' },
  'At Scene': { bg: '#FEF9C3', color: '#854D0E' },
  'At Hosp': { bg: '#FEF9C3', color: '#854D0E' },
}

const Section = ({ title, action, children }) => (
  <Paper
    variant="outlined"
    sx={{ borderRadius: 4, overflow: 'hidden', height: '100%' }}
  >
    <Stack
      direction="row"
      justifyContent="space-between"
      alignItems="center"
      sx={{
        px: 2,
        py: 1.5,
        backgroundColor: '#F8FAFC',
        borderBottom: '1px solid #E2E8F0',
      }}
    >
      <Typography fontWeight={900} color="#242F40">
        {title}
      </Typography>
      {action}
    </Stack>
    <Box sx={{ p: 2 }}>{children}</Box>
  </Paper>
)

// Employee-facing view of the CAD resource they're crewed on — the web
// counterpart of the mobile "Your resource" screen. Data comes from the same
// endpoints the app uses; membership in resource.users is the only gate, so
// crew can see their resource, crew mates and tasking before they clock in.
const MyResource = () => {
  const [loaded, setLoaded] = useState(false)
  const [activeResource, setActiveResource] = useState(null)
  const [messages, setMessages] = useState([])
  const [draft, setDraft] = useState('')
  const [sending, setSending] = useState(false)
  const chatEndRef = useRef(null)

  const resourceID = activeResource?.resource?.id

  const loadResource = () =>
    API.get('/mobile/command/my-active-resource')
      .then((res) => {
        if (res.status === 200) {
          setActiveResource(res.data?.activeResource ?? null)
        }
      })
      .catch((e) => console.log(e))
      .finally(() => setLoaded(true))

  useEffect(() => {
    loadResource()
    const interval = setInterval(loadResource, 15000)
    return () => clearInterval(interval)
  }, [])

  useEffect(() => {
    if (!resourceID) {
      setMessages([])
      return undefined
    }
    const loadChat = () =>
      API.get(`/mobile/command/resources/${resourceID}/chat`)
        .then((res) => {
          if (res.status === 200 && Array.isArray(res.data)) {
            setMessages(res.data)
          }
        })
        .catch((e) => console.log(e))
    loadChat()
    const interval = setInterval(loadChat, 8000)
    return () => clearInterval(interval)
  }, [resourceID])

  useEffect(() => {
    chatEndRef.current?.scrollIntoView({ block: 'nearest' })
  }, [messages.length])

  const sendMessage = () => {
    const message = draft.trim()
    if (!message || !resourceID || sending) return
    setSending(true)
    API.post(`/mobile/command/resources/${resourceID}/chat`, { message })
      .then((res) => {
        if (res.status === 200) {
          setDraft('')
          setMessages((prev) => [...prev, res.data])
        }
      })
      .catch((e) => console.log(e))
      .finally(() => setSending(false))
  }

  if (!loaded) {
    return (
      <Stack alignItems="center" sx={{ py: 8 }}>
        <CircularProgress />
      </Stack>
    )
  }

  if (!activeResource?.resource) {
    return (
      <Stack alignItems="center" spacing={2} sx={{ py: 8, textAlign: 'center' }}>
        <LocalPolice sx={{ fontSize: 56, color: '#94A3B8' }} />
        <Typography variant="h6" fontWeight={900} color="#242F40">
          No active resource
        </Typography>
        <Typography variant="body2" color="text.secondary" maxWidth={420}>
          You&apos;re not crewed on a resource in a live control room right
          now. When a dispatcher adds you to one, it will appear here with your
          crew mates and any tasking.
        </Typography>
        <Button href="/" sx={{ borderRadius: 999, textTransform: 'none', fontWeight: 800 }}>
          Back to Dashboard
        </Button>
      </Stack>
    )
  }

  const { resource, controlRoom, incident } = activeResource
  const roomName = controlRoom?.title || controlRoom?.event?.title || 'Control room'
  const statusStyle = statusChipStyles[resource.status] || {
    bg: '#E6F7F9',
    color: '#1E7F88',
  }

  return (
    <Grid container spacing={2}>
      <Grid item xs={12}>
        <Paper
          variant="outlined"
          sx={{
            borderRadius: 4,
            p: 2.5,
            borderLeft: '6px solid #239AA4',
          }}
        >
          <Stack
            direction={{ xs: 'column', sm: 'row' }}
            justifyContent="space-between"
            alignItems={{ sm: 'center' }}
            spacing={1.5}
          >
            <Stack direction="row" spacing={1.5} alignItems="center">
              <Avatar
                sx={{ backgroundColor: '#239AA4', width: 48, height: 48, borderRadius: 3 }}
              >
                <LocalPolice />
              </Avatar>
              <Stack>
                <Typography variant="h6" fontWeight={900} color="#242F40">
                  {resource.callSign}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  {roomName}
                  {controlRoom?.event?.title && controlRoom?.title
                    ? ` — ${controlRoom.event.title}`
                    : ''}
                </Typography>
              </Stack>
            </Stack>
            <Stack direction="row" spacing={1} flexWrap="wrap">
              <Chip
                label={resource.status}
                sx={{
                  borderRadius: 999,
                  fontWeight: 800,
                  backgroundColor: statusStyle.bg,
                  color: statusStyle.color,
                }}
              />
              {resource.type && (
                <Chip
                  label={resource.type}
                  sx={{ borderRadius: 999, fontWeight: 800 }}
                  variant="outlined"
                />
              )}
            </Stack>
          </Stack>
          {resource.note && (
            <Alert severity="info" sx={{ mt: 2, borderRadius: 3 }}>
              {resource.note}
            </Alert>
          )}
        </Paper>
      </Grid>

      <Grid item xs={12} md={incident ? 4 : 6}>
        <Section title="Your Crew">
          <Stack spacing={1.25}>
            {(resource.users ?? []).map((user) => (
              <Stack key={user.id} direction="row" spacing={1.25} alignItems="center">
                <Avatar
                  sx={{
                    width: 34,
                    height: 34,
                    borderRadius: 2,
                    backgroundColor: '#239AA4',
                  }}
                >
                  {(user.firstName || user.email || '?')[0]}
                </Avatar>
                <Typography fontWeight={800} color="#242F40">
                  {[user.firstName, user.lastName].filter(Boolean).join(' ') ||
                    user.email}
                </Typography>
              </Stack>
            ))}
            {!(resource.users ?? []).length && (
              <Typography variant="body2" color="text.secondary">
                No crew listed.
              </Typography>
            )}
          </Stack>
        </Section>
      </Grid>

      {incident && (
        <Grid item xs={12} md={4}>
          <Section
            title="Current Tasking"
            action={
              <Chip
                size="small"
                icon={<ReportProblem sx={{ fontSize: 16 }} />}
                label={incident.status}
                sx={{
                  borderRadius: 999,
                  fontWeight: 800,
                  backgroundColor: '#FEE2E2',
                  color: '#991B1B',
                }}
              />
            }
          >
            <Stack spacing={1}>
              <Typography fontWeight={900} color="#242F40">
                {incident.incidentNumber ? `#${incident.incidentNumber} — ` : ''}
                {incident.type || 'Incident'}
              </Typography>
              {incident.priority && (
                <Typography variant="body2" color="text.secondary">
                  Priority: <b>{incident.priority}</b>
                </Typography>
              )}
              {incident.location && (
                <Stack direction="row" spacing={0.75} alignItems="center">
                  <Place sx={{ fontSize: 18, color: '#64748B' }} />
                  <Typography variant="body2" color="text.secondary">
                    {incident.location}
                  </Typography>
                </Stack>
              )}
              {incident.presentingComplaint && (
                <Typography variant="body2" color="text.secondary">
                  {incident.presentingComplaint}
                </Typography>
              )}
              {incident.description && (
                <Typography variant="body2" color="text.secondary">
                  {incident.description}
                </Typography>
              )}
              <Divider />
              <Typography variant="caption" color="text.secondary">
                Assigned{' '}
                {incident.assignedAt
                  ? moment(incident.assignedAt).format('HH:mm')
                  : '—'}
                {' · '}Use the mobile app to acknowledge and update your status.
              </Typography>
            </Stack>
          </Section>
        </Grid>
      )}

      <Grid item xs={12} md={incident ? 4 : 6}>
        <Section title={`${roomName} Chat`}>
          <Stack spacing={1}>
            <Box sx={{ maxHeight: 320, overflowY: 'auto', pr: 0.5 }}>
              <Stack spacing={1}>
                {messages.map((chatMessage) => (
                  <Box
                    key={chatMessage._id}
                    sx={{
                      p: 1.25,
                      borderRadius: 3,
                      backgroundColor: chatMessage.fromControl
                        ? '#E6F7F9'
                        : '#F8FAFC',
                      border: '1px solid #E2E8F0',
                    }}
                  >
                    <Typography variant="caption" fontWeight={800} color="#1E7F88">
                      {chatMessage.fromControl
                        ? 'Control'
                        : chatMessage.authorName ||
                          [
                            chatMessage.author?.firstName,
                            chatMessage.author?.lastName,
                          ]
                            .filter(Boolean)
                            .join(' ') ||
                          'Crew'}
                      {' · '}
                      {moment(chatMessage.createdAt).format('HH:mm')}
                    </Typography>
                    <Typography variant="body2" color="#242F40">
                      {chatMessage.message}
                    </Typography>
                  </Box>
                ))}
                {!messages.length && (
                  <Typography variant="body2" color="text.secondary">
                    No messages yet.
                  </Typography>
                )}
                <div ref={chatEndRef} />
              </Stack>
            </Box>
            <Stack
              component="form"
              direction="row"
              spacing={1}
              onSubmit={(e) => {
                e.preventDefault()
                sendMessage()
              }}
            >
              <TextField
                fullWidth
                size="small"
                placeholder="Message control…"
                value={draft}
                onChange={(e) => setDraft(e.target.value)}
              />
              <IconButton
                type="submit"
                disabled={!draft.trim() || sending}
                sx={{ backgroundColor: '#E6F7F9', color: '#1E7F88' }}
              >
                <Send />
              </IconButton>
            </Stack>
          </Stack>
        </Section>
      </Grid>
    </Grid>
  )
}

export default MyResource
